import type { ManualJournal } from '../../types';
import { xeroImportState } from '../../lib/xeroImport';
import type { Moment } from './moments';

/**
 * Conversions' two accomplishment moments (docs/accomplishment-moments.md), each a PURE function from records the
 * server returned. They decide a fact; they never guess one. A missing or stale input means no moment.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * "DR-15 to Xero, done": the journal was proposed from a DR-15 extraction, exported, and its newest confirmation
 * stands. The last step is a person's word (the app cannot read Xero), so the detail says "you confirmed".
 */
export function dr15InXeroMoment(journal: ManualJournal, t: Translate): Moment | null {
  if (!journal.proposedFromExtractionRunId) return null;
  if (journal.supersededByJournalId) return null; // known to be wrong: its correction is the one that counts
  if (xeroImportState(journal) !== 'confirmed') return null;
  return {
    id: `dr15-in-xero:${journal.id}`,
    kind: 'dr15-in-xero',
    title: t('moments.dr15InXero.title'),
    detail: t('moments.dr15InXero.detail'),
    confetti: true,
  };
}

/**
 * "First journal for this client": `exported` is exported, and the client's list (refetched after the export)
 * shows it exported and shows no OTHER exported journal. `clientJournals` undefined = the list could not be read.
 */
export function firstJournalMoment(
  exported: ManualJournal,
  clientJournals: ManualJournal[] | undefined,
  clientName: string | undefined,
  t: Translate,
): Moment | null {
  if (exported.status !== 'exported' || exported.supersedesJournalId) return null;
  if (!clientJournals) return null;
  const live = clientJournals.filter((j) => !j.isDeleted && j.status === 'exported');
  if (!live.some((j) => j.id === exported.id)) return null; // a stale list cannot prove "first"
  if (live.some((j) => j.id !== exported.id)) return null;
  return {
    id: `first-journal:${exported.clientId}`,
    kind: 'first-journal',
    title: clientName
      ? t('moments.firstJournal.title', { client: clientName })
      : t('moments.firstJournal.titleNoName'),
    confetti: false,
  };
}
